/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { toOptions } from '../../utils/ManagerUtils';
import { defaultValue } from './CombinationChart';
import {
  chartDatalabelsAlign,
  chartDatalabelsAnchor,
} from 'componentregistry/registry/constants';

const CombinationChartManager: ComponentManagerFn<'CombinationChart'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CombinationChart'>(platform, 'CombinationChart'),
    type: 'CombinationChart',
    name: 'Combination Chart',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ChartJSTooltip',
          'Chartjs',
          'EventSubscription',
          'ExpressionWatchListener',
          'PulseTip',
          'TriggerOnClick',
        ] as ItemType[]
      ).includes(childType),
    defaultChildren: ['Chartjs', 'Chartjs'],
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      indexAxis: {
        label: 'Bar Direction',
        options: { x: 'Vertical', y: 'Horizontal' },
        type: 'select',
      },
      gridLines: {
        label: 'Grid Lines',
        options: {
          x: 'X Axis Only',
          y: 'Y Axis Only',
          xy: 'Both X & Y Axis',
          none: 'None',
        },
        type: 'select',
        required: true,
      },
      ticks: {
        label: 'Axis Labels',
        options: {
          x: 'X Axis Only',
          y: 'Y Axis Only',
          xy: 'Both X & Y Axis',
          none: 'None',
        },
        type: 'select',
        required: true,
      },
      xAxisLabel: {
        label: 'X Axis Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      yAxisLabel: {
        label: 'Y Axis Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      ryAxisLabel: {
        label: 'Right Y Axis Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      legendPosition: {
        label: 'Legend Position',
        options: toOptions(['left', 'top', 'right', 'bottom', 'hidden']),
        type: 'radio',
      },
      showBorder: {
        label: 'Show Borders',
        type: 'boolean',
      },
      xAxisLabelRotation: {
        label: 'X Axis Label Rotation',
        type: 'number',
        category: 'Data',
        step: 5,
        min: 0,
        max: 90,
      },
      labelFormatExpression: {
        label: 'X Axis Label Format Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        category: 'Data',
        helpText:
          'Use this expression to format the x axis label value. You can use $._.label to access the label value inside the expression.',
      },
      yAxisLabelFormatExpression: {
        label: 'Y Axis Label Format Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        category: 'Data',
        helpText:
          'Use this expression to format the y axis label value. You can use $._.label to access the label value inside the expression.',
      },
      yAxisStepSizeExpression: {
        label: 'Y Axis Step Size Expression',
        type: 'expressionOnly',
        returnType: () => 'number',
        category: 'Data',
        placeholder: 'e.g. Math.round($._.max / 3)',
        helpText:
          'Use this expression to return the step size for the y axis labels. You can use the min & max values as $._.min & $._.max in your expression. e.g. Math.round($._.max / 3)',
      },
      ryAxisLabelFormatExpression: {
        label: 'Right Y Axis Label Format Expression',
        type: 'expressionOnly',
        returnType: () => 'string',
        category: 'Data',
        helpText:
          'Use this expression to format the y axis label value. You can use $._.label to access the label value inside the expression.',
      },
      ryAxisStepSizeExpression: {
        label: 'Right Y Axis Step Size Expression',
        type: 'expressionOnly',
        returnType: () => 'number',
        category: 'Data',
        placeholder: 'e.g. Math.round($._.max / 3)',
        helpText:
          'Use this expression to return the step size for the y axis labels. You can use the min & max values as $._.min & $._.max in your expression. e.g. Math.round($._.max / 3)',
      },
      showMaxLine: {
        label: 'Show Max Line',
        type: 'boolean',
        category: 'Data',
      },
      showMinLine: {
        label: 'Show Min Line',
        type: 'boolean',
        category: 'Data',
      },
      showAvgLine: {
        label: 'Show Avg Line',
        type: 'boolean',
        category: 'Data',
      },
      hideTooltips: {
        label: 'Hide Tooltips',
        type: 'boolean',
        category: 'Data',
      },
      chartWidth: {
        label: 'Width',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's width. i.e. a value of 1 takes 100% of the container's width.",
      },
      chartHeight: {
        label: 'Height',
        max: 800,
        min: 0,
        step: 40,
        type: 'number',
        helpText:
          "Setting a value 1 or less will be considered as percentage of the parent container's height. i.e. a value of 1 takes 100% of the container's height.",
      },
      preRenderFunction: {
        label: 'Pre-render Callback',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText:
          'Use this callback function to customize the chart by manipulating the `data` & `options` passed to this function. Refer https://www.chartjs.org/docs/latest/configuration/ for various options you could use to customize the charts.',
      },
      enableDatalabels: {
        label: 'Enable Datalabels',
        type: 'boolean',
        category: 'Datalabels',
        helpText: 'Enable this to display data labels on chart element.',
      },
      anchor: {
        label: 'Anchor',
        type: 'select',
        options: chartDatalabelsAnchor,
        category: 'Datalabels',
        helpText: `An anchor point is to positon the data label on the chart element defined by the orientation.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#anchoring for more info.`,
      },
      align: {
        label: 'Align',
        type: 'select',
        options: chartDatalabelsAlign,
        category: 'Datalabels',
        helpText: `The align option defines the position of the label relative to the anchor point position and orientation.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#alignment-and-offset for more info.`,
      },
      offset: {
        label: 'Offset',
        type: 'number',
        category: 'Datalabels',
        helpText: `The offset represents the distance (in pixels) to pull the label away from the anchor point. 
        This option is not applicable when align is 'center'. 
        Also note that if align is 'start', the label is moved in the opposite direction. The default value is 4.
        Check this https://chartjs-plugin-datalabels.netlify.app/guide/positioning.html#alignment-and-offset for more info.`,
      },
      dlColorPallet: {
        label: 'Datalabels Color Pallete',
        type: 'expression',
        category: 'Datalabels',
        helpText: 'Comma seperated HTML Color codes in hex or rgb or rgba.',
        returnType: async () => 'string',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'align':
        case 'anchor':
        case 'dlColorPallet':
          return itemData.enableDatalabels;
        case 'offset':
          return itemData.enableDatalabels && itemData.align !== 'center';
        default:
          return true;
      }
    },
    category: 'Dashboard',
    icon: { iconName: 'chart-line' },
    excludeChildren: true,
    description: 'Combination Chart',
  };
};

export default CombinationChartManager;
